import type { AttackKind, Combatant, Impact } from './simulation'

export type CombatSide = 'player' | 'enemy'
export type AttackCounters = Record<AttackKind, number>
export type ImpactCounters = Record<Impact, number>
export type FighterTelemetry = {
  attacks: AttackCounters
  impacts: ImpactCounters
  damageDealt: number
  damageTaken: number
  maxCombo: number
  knockdowns: number
  guardBreaks: number
  parries: number
}
export type CombatTelemetry = {
  version: 'fight-telemetry/v1'
  elapsed: number
  frames: number
  player: FighterTelemetry
  enemy: FighterTelemetry
}
export type CombatTransition = {
  beforePlayer: Combatant
  afterPlayer: Combatant
  beforeEnemy: Combatant
  afterEnemy: Combatant
  dt: number
}

const attackCounters=():AttackCounters=>({light:0,heavy:0,special:0,throw:0})
const impactCounters=():ImpactCounters=>({whiff:0,hit:0,block:0,parry:0,'guard-break':0})
const fighterTelemetry=():FighterTelemetry=>({attacks:attackCounters(),impacts:impactCounters(),damageDealt:0,damageTaken:0,maxCombo:0,knockdowns:0,guardBreaks:0,parries:0})
export function createCombatTelemetry():CombatTelemetry{return{version:'fight-telemetry/v1',elapsed:0,frames:0,player:fighterTelemetry(),enemy:fighterTelemetry()}}
const cloneFighter=(f:FighterTelemetry):FighterTelemetry=>({...f,attacks:{...f.attacks},impacts:{...f.impacts}})
function attackStarted(before:Combatant,after:Combatant):AttackKind|null{
  if(after.attackPhase!=='startup'||after.lastAttack===null)return null
  if(before.attackPhase==='startup'&&before.lastAttack===after.lastAttack)return null
  return after.lastAttack
}
export function inferImpact(beforeAttacker:Combatant,afterAttacker:Combatant,beforeDefender:Combatant,afterDefender:Combatant):Impact|null{
  if(!afterAttacker.attackResolved||beforeAttacker.attackResolved)return null
  if(!afterAttacker.attackConnected)return'whiff'
  if(afterAttacker.hitstun>beforeAttacker.hitstun&&beforeDefender.parryWindow>0&&afterDefender.health===beforeDefender.health)return'parry'
  if(beforeDefender.blocking&&afterAttacker.lastAttack!=='throw'){
    if(!afterDefender.blocking&&afterDefender.guardMeter===38)return'guard-break'
    return'block'
  }
  return'hit'
}
export function recordCombatTransition(current:CombatTelemetry,transition:CombatTransition):CombatTelemetry{
  const dt=Number.isFinite(transition.dt)?Math.max(0,transition.dt):0
  const next:CombatTelemetry={...current,elapsed:current.elapsed+dt,frames:current.frames+1,player:cloneFighter(current.player),enemy:cloneFighter(current.enemy)}
  const playerAttack=attackStarted(transition.beforePlayer,transition.afterPlayer),enemyAttack=attackStarted(transition.beforeEnemy,transition.afterEnemy)
  if(playerAttack)next.player.attacks[playerAttack]++
  if(enemyAttack)next.enemy.attacks[enemyAttack]++
  const playerImpact=inferImpact(transition.beforePlayer,transition.afterPlayer,transition.beforeEnemy,transition.afterEnemy)
  const enemyImpact=inferImpact(transition.beforeEnemy,transition.afterEnemy,transition.beforePlayer,transition.afterPlayer)
  if(playerImpact){next.player.impacts[playerImpact]++;if(playerImpact==='parry')next.enemy.parries++;if(playerImpact==='guard-break')next.player.guardBreaks++}
  if(enemyImpact){next.enemy.impacts[enemyImpact]++;if(enemyImpact==='parry')next.player.parries++;if(enemyImpact==='guard-break')next.enemy.guardBreaks++}
  const playerDamage=Math.max(0,transition.beforeEnemy.health-transition.afterEnemy.health),enemyDamage=Math.max(0,transition.beforePlayer.health-transition.afterPlayer.health)
  next.player.damageDealt+=playerDamage;next.player.damageTaken+=enemyDamage;next.enemy.damageDealt+=enemyDamage;next.enemy.damageTaken+=playerDamage
  next.player.maxCombo=Math.max(next.player.maxCombo,transition.afterPlayer.comboCount);next.enemy.maxCombo=Math.max(next.enemy.maxCombo,transition.afterEnemy.comboCount)
  if(!transition.beforeEnemy.downed&&transition.afterEnemy.downed)next.player.knockdowns++
  if(!transition.beforePlayer.downed&&transition.afterPlayer.downed)next.enemy.knockdowns++
  return next
}
export type CombatQuality={accuracy:number;defenseRate:number;damagePerSecond:number;pressure:number}
export function combatQuality(fighter:FighterTelemetry,elapsed:number):CombatQuality{
  const attempts=Object.values(fighter.impacts).reduce((sum,value)=>sum+value,0),landed=fighter.impacts.hit+fighter.impacts.block+fighter.impacts.parry+fighter.impacts['guard-break'],defended=fighter.impacts.block+fighter.impacts.parry,safeElapsed=Math.max(Number.isFinite(elapsed)?elapsed:0,1/60)
  return{accuracy:attempts?landed/attempts:0,defenseRate:attempts?defended/attempts:0,damagePerSecond:fighter.damageDealt/safeElapsed,pressure:(fighter.guardBreaks*2+fighter.knockdowns*1.5+fighter.maxCombo)/safeElapsed}
}
export function telemetryInvariantErrors(telemetry:CombatTelemetry):string[]{
  const errors:string[]=[]
  if(!Number.isFinite(telemetry.elapsed)||telemetry.elapsed<0)errors.push('elapsed must be finite and non-negative')
  if(!Number.isSafeInteger(telemetry.frames)||telemetry.frames<0)errors.push('frames must be a non-negative safe integer')
  for(const [side,fighter] of [['player',telemetry.player],['enemy',telemetry.enemy]] as const){
    for(const [kind,count] of Object.entries(fighter.attacks))if(!Number.isSafeInteger(count)||count<0)errors.push(side+'.attacks.'+kind+' invalid')
    for(const [impact,count] of Object.entries(fighter.impacts))if(!Number.isSafeInteger(count)||count<0)errors.push(side+'.impacts.'+impact+' invalid')
    for(const field of ['damageDealt','damageTaken','maxCombo','knockdowns','guardBreaks','parries'] as const)if(!Number.isFinite(fighter[field])||fighter[field]<0)errors.push(side+'.'+field+' invalid')
  }
  return errors
}
